import { useState } from 'react';
import {
  Box, Flex, Text, Avatar, Switch, useToast,
} from '@chakra-ui/react';
import { LuCircleDollarSign, LuUser, LuPalette, LuSun, LuMoon, LuMonitor, LuPlug, LuLanguages, LuBell, LuToggleRight, LuTarget, LuGauge } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useColorPreference } from '../../contexts/ColorPreferenceContext';
import { usePreferences } from '../../contexts/PreferencesContext';
import { useNavigate } from 'react-router-dom';
import { SUPPORTED_LANGUAGES } from '../../i18n/languages';
import { ALERT_KEYS } from '../../utils/movementAlerts';
import AiUsageBars from './AiUsageBars';
import CurrenciesContent from './CurrenciesContent';
import MCPContent from './MCPContent';


import Card, { cardSurface } from '../shared/Card';
const SECTION_KEYS = [
  { key: 'account',    icon: LuUser },
  { key: 'plan',       icon: LuGauge },
  { key: 'appearance', icon: LuPalette },
  { key: 'language',   icon: LuLanguages },
  { key: 'alerts',     icon: LuBell },
  { key: 'currencies', icon: LuCircleDollarSign },
  { key: 'features',   icon: LuToggleRight },

  { key: 'mcp',        icon: LuPlug },
];

function NavItem({ sectionKey, icon: Icon, label, isActive, onClick }) {
  return (
    <Flex
      as="button"
      type="button"
      align="center"
      gap={{ base: 2, md: 3 }}
      px={3}
      py="9px"
      borderRadius="lg"
      w={{ base: 'auto', md: '100%' }}
      flexShrink={0}
      whiteSpace="nowrap"
      textAlign="left"
      bg={isActive ? 'redesign.bg.hover' : 'transparent'}
      color={isActive ? 'redesign.heading' : 'redesign.text.muted'}
      fontWeight={isActive ? 600 : 400}
      fontSize="sm"
      _hover={{ bg: 'redesign.bg.hover', color: 'redesign.heading' }}
      transition="all 0.15s"
      onClick={onClick}
    >
      <Box as={Icon} boxSize="16px" flexShrink={0} />
      {label}
    </Flex>
  );
}

function Row({ label, description, action }) {
  return (
    <Flex align="center" justify="space-between" py={4} borderBottom="1px solid" borderColor="redesign.border" _last={{ borderBottom: 'none' }}>
      <Box>
        <Text fontSize="sm" fontWeight={500} color="redesign.heading">{label}</Text>
        {description && <Text fontSize="xs" color="redesign.text.muted" mt={0.5}>{description}</Text>}
      </Box>
      {action}
    </Flex>
  );
}

function SectionHeading({ children }) {
  return (
    <Text textStyle="microLabel" mb={2} mt={6}>
      {children}
    </Text>
  );
}

function AccountSection({ user, onLogout }) {
  const toast = useToast();
  const { t } = useTranslation(['settings', 'common']);

  const handleCopy = () => {
    navigator.clipboard.writeText(user?.email || '');
    toast({ description: t('account.emailCopied'), status: 'success', duration: 2000, isClosable: true });
  };

  return (
    <Box maxW="560px">
      {/* Avatar + name */}
      <Flex align="center" gap={4} mb={6}>
        <Avatar
          src={user?.picture}
          name={user?.full_name || user?.username}
          size="lg"
          bg="redesign.bg.hover"
          color="redesign.text"
        />
        <Box>
          <Text fontWeight={600} fontSize="lg" color="redesign.heading">
            {user?.full_name || user?.username || '—'}
          </Text>
          <Text fontSize="sm" color="redesign.text.muted">{user?.email || '—'}</Text>
        </Box>
      </Flex>

      <SectionHeading>{t('account.information')}</SectionHeading>

      <Card borderRadius="xl" px={4}>
        <Row
          label={t('account.email')}
          action={
            <Flex align="center" gap={2}>
              <Text fontSize="sm" color="redesign.text.muted">{user?.email}</Text>
              <Card
                as="button"
                type="button"
                px={3}
                py={1}
                fontSize="xs"
                fontWeight={500}
                borderRadius="md"
                color="redesign.text"
                _hover={{ bg: 'redesign.bg.hover' }}
                onClick={handleCopy}
              >
                {t('common:actions.copy')}
              </Card>
            </Flex>
          }
        />
        <Row
          label={t('account.username')}
          action={<Text fontSize="sm" color="redesign.text.muted">{user?.username || '—'}</Text>}
        />
      </Card>

      <SectionHeading>{t('account.actions')}</SectionHeading>

      <Card borderRadius="xl" px={4}>
        <Row
          label={t('account.logout')}
          description={t('account.logoutDescription')}
          action={
            <Card
              as="button"
              type="button"
              px={3}
              py={1}
              fontSize="xs"
              fontWeight={500}
              borderRadius="md"
              color="redesign.text"
              _hover={{ bg: 'redesign.bg.hover' }}
              onClick={onLogout}
            >
              {t('account.logout')}
            </Card>
          }
        />
      </Card>
    </Box>
  );
}

// The person's plan (MI-354): for now only how much of their AI budget they
// have used. Where their plan will show once the app has prices.
function PlanSection() {
  const { t } = useTranslation('settings');
  return (
    <Box maxW="560px">
      <SectionHeading>{t('plan.usage')}</SectionHeading>
      <Card borderRadius="xl" px={4} py={1}>
        <AiUsageBars />
      </Card>
      <Text fontSize="xs" color="redesign.text.muted" mt={3}>{t('plan.description')}</Text>
    </Box>
  );
}

const THEME_OPTIONS = [
  { value: 'system', labelKey: 'appearance.themeOptions.system', icon: LuMonitor },
  { value: 'light',  labelKey: 'appearance.themeOptions.light',  icon: LuSun },
  { value: 'dark',   labelKey: 'appearance.themeOptions.dark',   icon: LuMoon },
];

function ThemeSegmentedControl() {
  const { preference, setPreference } = useColorPreference();
  const { t } = useTranslation('settings');

  return (
    <Flex
      role="radiogroup"
      aria-label={t('appearance.themeAriaLabel')}
      bg="redesign.bg.sunken"
      border="1px solid"
      borderColor="redesign.border"
      borderRadius="8px"
      p="2px"
      gap="2px"
    >
      {THEME_OPTIONS.map((opt) => {
        const Icon = opt.icon;
        const isActive = preference === opt.value;
        return (
          <Flex
            key={opt.value}
            as="button"
            type="button"
            role="radio"
            aria-checked={isActive}
            onClick={() => setPreference(opt.value)}
            align="center"
            gap={2}
            px={3}
            py="6px"
            borderRadius="6px"
            fontSize="sm"
            fontWeight={isActive ? 600 : 500}
            bg={isActive ? 'redesign.bg.raised' : 'transparent'}
            color={isActive ? 'redesign.heading' : 'redesign.text.muted'}
            boxShadow={isActive ? '0 1px 2px rgba(16, 24, 40, 0.10)' : 'none'}
            _dark={{ boxShadow: 'none' }}
            _hover={!isActive ? { color: 'redesign.heading' } : undefined}
            transition="all 0.15s"
          >
            <Box as={Icon} boxSize="14px" />
            {t(opt.labelKey)}
          </Flex>
        );
      })}
    </Flex>
  );
}

function AppearanceSection() {
  const { t } = useTranslation('settings');

  return (
    <Box maxW="560px">
      <Text fontWeight={600} fontSize="lg" color="redesign.heading" mb={1}>
        {t('appearance.title')}
      </Text>
      <Text fontSize="sm" color="redesign.text.muted" mb={6}>
        {t('appearance.subtitle')}
      </Text>

      <SectionHeading>{t('appearance.themeHeading')}</SectionHeading>

      <Card
        borderRadius="xl"
        px={4}
      >
        <Row
          label={t('appearance.themeRowLabel')}
          description={t('appearance.themeDescription')}
          action={<ThemeSegmentedControl />}
        />
      </Card>
    </Box>
  );
}

function LanguageSegmentedControl() {
  const { t, i18n } = useTranslation('settings');
  const { setLanguage } = usePreferences();
  const currentLng = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];

  return (
    <Flex
      role="radiogroup"
      aria-label={t('language.ariaLabel')}
      bg="redesign.bg.sunken"
      border="1px solid"
      borderColor="redesign.border"
      borderRadius="8px"
      p="2px"
      gap="2px"
    >
      {SUPPORTED_LANGUAGES.map((opt) => {
        const isActive = currentLng === opt.code;
        return (
          <Flex
            key={opt.code}
            as="button"
            type="button"
            role="radio"
            aria-checked={isActive}
            onClick={() => setLanguage(opt.code)}
            align="center"
            gap={2}
            px={3}
            py="6px"
            borderRadius="6px"
            fontSize="sm"
            fontWeight={isActive ? 600 : 500}
            bg={isActive ? 'redesign.bg.raised' : 'transparent'}
            color={isActive ? 'redesign.heading' : 'redesign.text.muted'}
            boxShadow={isActive ? '0 1px 2px rgba(16, 24, 40, 0.10)' : 'none'}
            _dark={{ boxShadow: 'none' }}
            _hover={!isActive ? { color: 'redesign.heading' } : undefined}
            transition="all 0.15s"
          >
            {opt.nativeLabel}
          </Flex>
        );
      })}
    </Flex>
  );
}

function LanguageSection() {
  const { t } = useTranslation('settings');

  return (
    <Box maxW="560px">
      <Text fontWeight={600} fontSize="lg" color="redesign.heading" mb={1}>
        {t('language.title')}
      </Text>
      <Text fontSize="sm" color="redesign.text.muted" mb={6}>
        {t('language.subtitle')}
      </Text>

      <SectionHeading>{t('language.heading')}</SectionHeading>

      <Card
        borderRadius="xl"
        px={4}
      >
        <Row
          label={t('language.rowLabel')}
          description={t('language.rowDescription')}
          action={<LanguageSegmentedControl />}
        />
      </Card>
    </Box>
  );
}

function AlertsSection() {
  const { t } = useTranslation('settings');
  const { isAlertEnabled, setAlertEnabled, allAlertsEnabled, setAllAlerts } = usePreferences();

  const surface = {
    ...cardSurface,
    px: 4,
  };

  return (
    <Box maxW="560px">
      <Text fontWeight={600} fontSize="lg" color="redesign.heading" mb={1}>
        {t('alerts.title')}
      </Text>
      <Text fontSize="sm" color="redesign.text.muted" mb={6}>
        {t('alerts.subtitle')}
      </Text>

      <Box {...surface}>
        <Row
          label={t('alerts.all.label')}
          description={t('alerts.all.description')}
          action={
            <Switch
              isChecked={allAlertsEnabled}
              onChange={(e) => setAllAlerts(e.target.checked)}
              colorScheme="blue"
              aria-label={t('alerts.all.label')}
            />
          }
        />
      </Box>

      <SectionHeading>{t('alerts.heading')}</SectionHeading>

      <Box {...surface}>
        {ALERT_KEYS.map((key) => (
          <Row
            key={key}
            label={t(`alerts.rows.${key}.label`)}
            description={t(`alerts.rows.${key}.description`)}
            action={
              <Switch
                isChecked={isAlertEnabled(key)}
                onChange={(e) => setAlertEnabled(key, e.target.checked)}
                colorScheme="blue"
                aria-label={t(`alerts.rows.${key}.label`)}
              />
            }
          />
        ))}
      </Box>
    </Box>
  );
}

function FeaturesSection() {
  const { t } = useTranslation('settings');
  const { isFeatureEnabled, setFeatureEnabled } = usePreferences();

  return (
    <Box maxW="560px">
      <Text fontWeight={600} fontSize="lg" color="redesign.heading" mb={1}>
        {t('features.title')}
      </Text>
      <Text fontSize="sm" color="redesign.text.muted" mb={6}>
        {t('features.subtitle')}
      </Text>

      <Card borderRadius="xl" px={4}>
        <Row
          label={
            <Flex as="span" align="center" gap={2}>
              <Box as={LuTarget} boxSize="14px" />
              {t('features.goals.label')}
            </Flex>
          }
          description={t('features.goals.description')}
          action={
            <Switch
              isChecked={isFeatureEnabled('goals')}
              onChange={(e) => setFeatureEnabled('goals', e.target.checked)}
              colorScheme="blue"
              aria-label={t('features.goals.label')}
            />
          }
        />
      </Card>
    </Box>
  );
}

export default function SettingsPage({ onClose, initialSection = 'account' }) {
  const [activeSection, setActiveSection] = useState(initialSection);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('settings');

  const handleLogout = () => {
    logout();
    navigate('/login');
    onClose?.();
  };

  return (
    <Flex
      direction={{ base: 'column', md: 'row' }}
      h="100%"
      w="100%"
      overflow="hidden"
    >
      {/* Nav: vertical sidebar on desktop, horizontal pill strip on mobile */}
      <Flex
        direction={{ base: 'row', md: 'column' }}
        w={{ base: '100%', md: '180px' }}
        flexShrink={0}
        overflowX={{ base: 'auto', md: 'visible' }}
        borderRightWidth={{ base: 0, md: '1px' }}
        borderBottomWidth={{ base: '1px', md: 0 }}
        borderStyle="solid"
        borderColor="redesign.border"
        bg="redesign.bg.surface"
        px={{ base: 2, md: 2 }}
        py={{ base: 2, md: 5 }}
        gap={{ base: 1, md: 0 }}
      >
        <Text
          display={{ base: 'none', md: 'block' }}
          textStyle="microLabel"
          px={3}
          mb={3}
        >
          {t('title')}
        </Text>
        <Flex
          direction={{ base: 'row', md: 'column' }}
          flex={{ md: 1 }}
          gap={{ base: 1, md: 0 }}
        >
          {SECTION_KEYS.map(s => (
            <NavItem
              key={s.key}
              sectionKey={s.key}
              icon={s.icon}
              label={t(`sections.${s.key}`)}
              isActive={activeSection === s.key}
              onClick={() => setActiveSection(s.key)}
            />
          ))}
        </Flex>
      </Flex>

      {/* Content */}
      <Box
        flex={1}
        overflowY="auto"
        px={{ base: 4, md: 6 }}
        py={{ base: 4, md: 6 }}
        minW={0}
        bg="redesign.bg.surface"
      >
        {activeSection === 'account' && (
          <AccountSection user={user} onLogout={handleLogout} />
        )}
        {activeSection === 'plan' && (
          <PlanSection />
        )}
        {activeSection === 'appearance' && (
          <AppearanceSection />
        )}
        {activeSection === 'language' && (
          <LanguageSection />
        )}
        {activeSection === 'alerts' && (
          <AlertsSection />
        )}
        {activeSection === 'currencies' && (
          <CurrenciesContent />
        )}
        {activeSection === 'features' && (
          <FeaturesSection />
        )}

        {activeSection === 'mcp' && (
          <MCPContent />
        )}
      </Box>
    </Flex>
  );
}
